import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Role } from '../../../core/enum/role.enum';
import { EkycStatus } from '../../../core/enum/ekyc-status.enum';
import { PaymentMethod } from '../../../core/models/payment/payment-method';
import { UserProfile } from '../../../core/models/user/user-profile';
import { IdentitySectionFormComponent } from '../../../shared/profile-section-forms/identity-section-form/identity-section-form.component';
import { ContactSectionFormComponent } from '../../../shared/profile-section-forms/contact-section-form/contact-section-form.component';
import { EmploymentSectionFormComponent } from '../../../shared/profile-section-forms/employment-section-form/employment-section-form.component';
import { IncomeTransactionSectionFormComponent } from '../../../shared/profile-section-forms/income-transaction-section-form/income-transaction-section-form.component';
import { BankSectionFormComponent } from '../../../shared/profile-section-forms/bank-section-form/bank-section-form.component';
import { ReviewAttestSection } from '../section-status.util';

// INC-289 Phase 2: bottom-sheet host for editing ONE review-attest section in
// place. Built on NgbModal (like hold-breakdown-sheet) so the full WAI-ARIA
// APG dialog contract — role=dialog + aria-modal, focus-trap, Esc-close,
// return-focus, background-inert, body scroll-lock — comes for free; we only
// add bottom-fixed windowClass chrome (section-edit-sheet.scss), the grab
// handle, and aria-labelledby → the title id.
//
// It embeds the SAME section-form components the standalone settings pages
// use, so validation/error-message parity with those pages is automatic
// (one definition of the rules — the whole point of Phase 1's extraction).
// The sheet never persists: Save closes the modal with the edited form value
// as the payload; the opener (ProfileReviewAttestComponent) owns the API call
// and the re-KYC routing. Discard-confirm on a dirty cancel/Esc/backdrop is
// wired by the opener via NgbModalOptions.beforeDismiss (which is invoked for
// programmatic dismiss too, in this ng-bootstrap version).
@Component({
  standalone: false,
  selector: 'app-section-edit-sheet',
  templateUrl: './section-edit-sheet.component.html',
})
export class SectionEditSheetComponent {
  section: ReviewAttestSection;
  profile: UserProfile;
  paymentMethod: PaymentMethod;
  role: Role;

  showFormError = false;

  // Shared with the sheet template's aria-labelledby and the opener's
  // NgbModalOptions.ariaLabelledBy so both reference the same title element.
  readonly titleId = 'section-edit-sheet-title';

  EkycStatus: typeof EkycStatus = EkycStatus;

  @ViewChild(IdentitySectionFormComponent) identitySectionForm?: IdentitySectionFormComponent;
  @ViewChild(ContactSectionFormComponent) contactSectionForm?: ContactSectionFormComponent;
  @ViewChild(EmploymentSectionFormComponent) employmentSectionForm?: EmploymentSectionFormComponent;
  @ViewChild(IncomeTransactionSectionFormComponent)
  incomeTransactionSectionForm?: IncomeTransactionSectionFormComponent;
  @ViewChild(BankSectionFormComponent) bankSectionForm?: BankSectionFormComponent;

  constructor(public modal: NgbActiveModal) {}

  get title(): string {
    switch (this.section) {
      case 'identity':
        return 'Edit identity';
      case 'contact':
        return 'Edit contact';
      case 'employmentIncome':
        return 'Edit employment & income';
      case 'bank':
        return 'Edit bank';
    }
  }

  // Sub-note under the sheet title (Variant B). Compliance-sensitive strings for
  // identity/income/bank; everything else reassures that edits save straight away.
  get subNote(): string {
    switch (this.section) {
      case 'identity':
        return 'Changing your name or ID starts a short re-verification — your current details stay active until it passes.';
      case 'employmentIncome':
        return 'Purpose of transaction is required under our KYC obligations.';
      case 'bank':
        return 'Your bank account should be in your own name.';
      default:
        return 'Updates save straight away.';
    }
  }

  // The active child form(s) for the current section. Most sections have a
  // single form; the merged employment & income section renders TWO, so this
  // returns an array. Resolves after view init; Save/Cancel/isDirty are only
  // reachable after the sheet is on screen, so the ViewChildren are populated
  // by then. Undefined forms are filtered out.
  private activeForms(): UntypedFormGroup[] {
    let forms: (UntypedFormGroup | undefined)[];
    switch (this.section) {
      case 'identity':
        forms = [this.identitySectionForm?.form];
        break;
      case 'contact':
        forms = [this.contactSectionForm?.form];
        break;
      case 'employmentIncome':
        forms = [this.employmentSectionForm?.form, this.incomeTransactionSectionForm?.form];
        break;
      case 'bank':
        forms = [this.bankSectionForm?.form];
        break;
      default:
        forms = [];
    }
    return forms.filter((form): form is UntypedFormGroup => form != null);
  }

  // Read by the opener's beforeDismiss guard to decide whether a discard
  // confirm is needed. patchValue-seeding does not mark a control dirty, so a
  // freshly opened, untouched sheet reports clean.
  isDirty(): boolean {
    return this.activeForms().some((form) => form.dirty);
  }

  save(): void {
    const forms = this.activeForms();

    // Mirror the standalone pages' submit gate: surface every field's error
    // and block on invalid, rather than silently doing nothing. For the merged
    // section this touches BOTH forms and blocks if EITHER is invalid.
    forms.forEach((form) => form.markAllAsTouched());
    this.showFormError = true;

    if (forms.some((form) => form.invalid)) {
      return;
    }

    // Close with the MERGED value of every active form (a single form's value
    // for the simple sections; both sub-forms combined for employment & income).
    this.modal.close(Object.assign({}, ...forms.map((form) => form.value)));
  }

  cancel(): void {
    // Routes through the opener's beforeDismiss guard (dirty → discard
    // confirm) exactly like Esc/backdrop.
    this.modal.dismiss();
  }
}
